/* stylelint-disable named-grid-areas-oinvalid */
import { useRef } from 'react';
import { useMeasure } from 'react-use';
import styled, { css } from 'styled-components';

import {
  desktopAndAbove,
  laptopAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { Curve } from './curve.tsx';
import { GameCell, Team } from './game.tsx';

// []⎤
//   ├ []⎤
// []⎦   ⎪
//       ├ []
// []⎤   ⎪
//   ├ []⎦
// []⎦

export enum TournamentAlignment {
  LEFT,
  CENTER,
  RIGHT,
}

const Wrapper = styled.div`
  container-type: inline-size;
  padding: 0 var(--unit);
`;

const GameWrapper = styled.div`
  display: grid;
  align-content: center;
`;

const getGridLayout = ({
  alignment,
  isLeaf,
}: {
  alignment: TournamentAlignment;
  isLeaf: boolean;
}) => {
  if (isLeaf) {
    return `'A'`;
  }
  if (alignment === TournamentAlignment.CENTER) {
    return `'B A C'`;
  }
  if (alignment === TournamentAlignment.RIGHT) {
    return `'A B' 'A C'`;
  }
  return `'B A' 'C A'`;
};

const TournamentGameWrapper = styled.div<{
  alignment: TournamentAlignment;
  isLeaf: boolean;
}>`
  --spacing: 16px;

  @media ${laptopAndAbove} {
    --spacing: 24px;
  }
  @media ${desktopAndAbove} {
    --spacing: 32px;
  }

  position: relative;

  ${({ isLeaf }) =>
    !isLeaf &&
    css`
      gap: var(--spacing);
    `};
  width: 100%;
  display: grid;
  grid-template-areas: ${getGridLayout};
  isolation: isolate;

  svg {
    position: absolute;
    inset: 0;
    z-index: -1;
  }
  & > &:nth-of-type(1) {
    grid-area: B;
  }
  & > &:nth-of-type(2) {
    grid-area: C;
  }
  ${GameWrapper} {
    grid-area: A;
  }
`;

type TeamType = {
  slug: string;
  name: string;
  shortName: string;
  pictureUrl: string;
};

export type TournamentGameType = {
  slot1?: TeamType;
  slot1Score?: number;
  slot1Parent?: TournamentGameType;

  slot2?: TeamType;
  slot2Parent?: TournamentGameType;
  slot2Score?: number;
};

type Props = {
  game: TournamentGameType;
  winner?: TeamType;
  alignment?: TournamentAlignment;
};

const TournamentGame = ({
  game,
  winner,
  alignment = TournamentAlignment.LEFT,
}: Props) => {
  const [ref, { width, height }] = useMeasure<HTMLDivElement>();

  const gameRef = useRef<HTMLDivElement | null>(null);
  const slot1Ref = useRef<HTMLDivElement | null>(null);
  const slot2Ref = useRef<HTMLDivElement | null>(null);
  const slot1ParentRef = useRef<HTMLDivElement | null>(null);
  const slot2ParentRef = useRef<HTMLDivElement | null>(null);

  const isLeaf =
    game.slot1Parent === undefined && game.slot2Parent === undefined;

  const winnerIsSlot1 = winner && game.slot1?.slug === winner.slug;
  const winnerIsSlot2 = winner && game.slot2?.slug === winner.slug;

  const highlightPath1 = game.slot1 && !winnerIsSlot2;
  const highlightPath2 = game.slot2 && !winnerIsSlot1;

  // ensure highlighted path is painted on top
  const path1 = (
    <Curve
      fromRef={slot1Ref}
      toRef={slot1ParentRef}
      stroke={highlightPath1 ? '#fff' : '#333'}
    />
  );
  const path2 = (
    <Curve
      fromRef={slot2Ref}
      toRef={slot2ParentRef}
      stroke={highlightPath2 ? '#fff' : '#333'}
    />
  );
  const paths = highlightPath1 ? (
    <>
      {path2}
      {path1}
    </>
  ) : (
    <>
      {path1}
      {path2}
    </>
  );

  return (
    <TournamentGameWrapper alignment={alignment} isLeaf={isLeaf} ref={ref}>
      {!isLeaf && (
        <>
          <svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            fill="transparent"
            strokeWidth="4"
          >
            {paths}
          </svg>
          {!!game.slot1Parent && (
            <div ref={slot1ParentRef}>
              <TournamentGame
                game={game.slot1Parent}
                winner={game.slot1}
                alignment={
                  alignment === TournamentAlignment.CENTER
                    ? TournamentAlignment.LEFT
                    : alignment
                }
              />
            </div>
          )}
          {!!game.slot2Parent && (
            <div ref={slot2ParentRef}>
              <TournamentGame
                game={game.slot2Parent}
                winner={game.slot2}
                alignment={
                  alignment === TournamentAlignment.CENTER
                    ? TournamentAlignment.RIGHT
                    : alignment
                }
              />
            </div>
          )}
        </>
      )}
      <GameWrapper ref={gameRef}>
        <GameCell big={alignment === TournamentAlignment.CENTER}>
          <Team
            ref={slot1Ref}
            team={game.slot1}
            winner={winnerIsSlot1}
            hasLost={winnerIsSlot2 || false}
          />
          <Team
            ref={slot2Ref}
            team={game.slot2}
            winner={winnerIsSlot2}
            hasLost={winnerIsSlot1 || false}
          />
        </GameCell>
      </GameWrapper>
    </TournamentGameWrapper>
  );
};

export const Bracket = ({
  game,
  alignment = TournamentAlignment.LEFT,
  winner,
}: Props) => {
  return (
    <Wrapper>
      <TournamentGame game={game} alignment={alignment} winner={winner} />
    </Wrapper>
  );
};
